
export default function ComparisonSection() {
  return (
    <section className="mb-16">
      {/* Pull Quote */}
      <div className="bg-gray-900 rounded-2xl p-8 md:p-12 text-center mb-16">
        <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="ri-double-quotes-l text-3xl text-white"></i>
        </div>
        <blockquote className="text-xl md:text-2xl text-white font-medium mb-6 leading-relaxed max-w-3xl mx-auto">
          &ldquo;Monthly bookkeeping is not an expense—it is an investment that pays for itself many times over through better decisions, fewer penalties, and peace of mind.&rdquo;
        </blockquote>
        <div className="flex items-center justify-center gap-4">
          <div className="w-12 h-12 rounded-full overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/004027971fce597169e9d10012dea3ce.jpg"
              alt="el-paso-bookkeeping-professional-testimonial"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-left">
            <p className="text-white font-semibold">Carlos Mendoza, CPA</p>
            <p className="text-gray-400 text-sm">El Paso Financial Advisory</p>
          </div>
        </div>
      </div>

      {/* Monthly vs Annual Comparison */}
      <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Monthly Bookkeeping vs. Annual Catch-Up: A Side-by-Side Look</h3>
      <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto leading-relaxed">
        Still wondering whether monthly bookkeeping is worth the commitment? Here is how the two approaches compare for a typical <strong>El Paso TX</strong> small business.
      </p>

      <div className="overflow-x-auto mb-12">
        <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
          <thead>
            <tr className="bg-gray-900 text-white">
              <th className="text-left px-6 py-4 text-sm font-semibold">Factor</th>
              <th className="text-center px-6 py-4 text-sm font-semibold">
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-calendar-check-line text-teal-400"></i>
                  Monthly Bookkeeping
                </div>
              </th>
              <th className="text-center px-6 py-4 text-sm font-semibold">
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-calendar-close-line text-red-400"></i>
                  Annual Catch-Up
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              { factor: "Cash-flow awareness", monthly: "Real-time, every 30 days", annual: "Months-old data at best" },
              { factor: "Tax preparation cost", monthly: "$800–$1,500 / year", annual: "$2,500–$5,000+ / year" },
              { factor: "Error detection speed", monthly: "Within 30 days", annual: "6–12 months later" },
              { factor: "Loan readiness", monthly: "Always current", annual: "Weeks to prepare" },
              { factor: "IRS audit risk", monthly: "Minimal—clean records", annual: "Higher—gaps and errors" },
              { factor: "Owner time spent", monthly: "1–2 hours / month", annual: "40+ hours / year" },
              { factor: "Missed deductions", monthly: "Rare—expenses tracked live", annual: "Common—receipts lost" },
              { factor: "Business growth insight", monthly: "Monthly trend analysis", annual: "Hindsight only" },
            ].map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.factor}</td>
                <td className="px-6 py-4 text-sm text-center text-teal-700 font-medium">{row.monthly}</td>
                <td className="px-6 py-4 text-sm text-center text-gray-500">{row.annual}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="bg-teal-50 rounded-xl p-6 text-center border border-teal-100">
          <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">$4,200</p>
          <p className="text-gray-600 text-xs leading-relaxed">Average annual savings from catching billing errors early</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-6 text-center border border-orange-100">
          <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-time-line text-2xl text-orange-500"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">38 hrs</p>
          <p className="text-gray-600 text-xs leading-relaxed">Time saved per year by outsourcing monthly bookkeeping</p>
        </div>
        <div className="bg-green-50 rounded-xl p-6 text-center border border-green-100">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-percent-line text-2xl text-green-600"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">27%</p>
          <p className="text-gray-600 text-xs leading-relaxed">More deductions claimed by businesses with monthly books</p>
        </div>
        <div className="bg-red-50 rounded-xl p-6 text-center border border-red-100">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-shield-check-line text-2xl text-red-500"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">82%</p>
          <p className="text-gray-600 text-xs leading-relaxed">Of failed small businesses cite cash-flow issues as the cause</p>
        </div>
      </div>
    </section>
  );
}
